import type { TournamentMatch } from "./api";

/** Rótulo del lado del cuadro que ya nunca tendrá jugador: su fuente terminó
 * en Doble W.O. (o de ella no sale perdedor). Antes decía «Por definir». */
export const DEAD_SIDE_LABEL = "Sin rival · Doble W.O.";

const FEED = /\[p([12])_from=([^\]#]+)#(\d+)(:perdedor)?\]/g;
const DESTINO = /Destino: partido (\d+) · lado ([12])/;
const JORNADA = /^(?:semana|jornada|matchups?|j)\s*#?\s*(\d+)/i;

function key(match: TournamentMatch, side: 1 | 2): string {
  return `${match.id}:${side}`;
}

function isDead(source: TournamentMatch, wantsLoser: boolean): boolean {
  if (source.status === "double_walkover") return true;
  if (!wantsLoser || source.winner_participant_id == null) return false;
  const loser = [source.player1_participant_id, source.player2_participant_id].find(
    (pid) => pid != null && pid !== source.winner_participant_id,
  );
  return loser == null;
}

/** Lados vacíos que ya no se llenarán, como `${matchId}:${lado}`. Mismas
 * lecturas que el avance del servidor: [pN_from] en el destino y el
 * «Destino» de la preliminar. */
export function deadBracketSides(matches: TournamentMatch[]): Set<string> {
  const live = matches
    .filter((m) => m.status !== "cancelled" && !m.parent_match_id)
    .sort((a, b) => a.id - b.id);
  // Filas repetidas en (ronda, número): como el servidor, la primera por id y,
  // si hay, la del mismo grupo que el destino.
  const byKeyAll = new Map<string, TournamentMatch[]>();
  for (const m of live) {
    if (m.match_number == null) continue;
    const k = `${m.division_id ?? ""}|${(m.round_label ?? "").trim()}|${m.match_number}`;
    byKeyAll.set(k, [...(byKeyAll.get(k) ?? []), m]);
  }
  const pick = (k: string, row: TournamentMatch): TournamentMatch | undefined => {
    const found = (byKeyAll.get(k) ?? []).filter((m) => m.id !== row.id);
    return found.find((m) => m.group_id === row.group_id) ?? found[0];
  };
  const dead = new Set<string>();
  for (const row of live) {
    for (const found of (row.notes ?? "").matchAll(FEED)) {
      const side = Number(found[1]) as 1 | 2;
      const occupant = side === 1 ? row.player1_participant_id : row.player2_participant_id;
      const source = pick(`${row.division_id ?? ""}|${found[2].trim()}|${Number(found[3])}`, row);
      if (occupant == null && source && isDead(source, Boolean(found[4]))) dead.add(key(row, side));
    }
  }
  for (const source of live) {
    const destination = DESTINO.exec(source.notes ?? "");
    const jornada = JORNADA.exec((source.round_label ?? "").trim());
    if (!destination || !jornada || source.group_id != null || !isDead(source, false)) continue;
    const target = (byKeyAll.get(`${source.division_id ?? ""}|Jornada ${Number(jornada[1]) + 1}|${Number(destination[1])}`) ?? [])
      .find((m) => m.group_id == null);
    if (!target) continue;
    const side = Number(destination[2]) as 1 | 2;
    const occupant = side === 1 ? target.player1_participant_id : target.player2_participant_id;
    if (occupant == null) dead.add(key(target, side));
  }
  return dead;
}

export function isDeadSide(dead: Set<string>, match: TournamentMatch, side: 1 | 2): boolean {
  return dead.has(key(match, side));
}
